import { engineComponent } from "neo-common";
import { HtmlContainerElement } from "./HtmlContainerElement";
import { HtmlController } from "../HtmlController";
import { ComponentTypes } from "../../Components/ComponentTypes";
import { Scaling } from "../../Utils/Scaling";
import { AppContext } from "../../../Common/AppContext";
import { DataManager } from "../../Managers/DataManager";
import { IButtonElement } from "../../CustomComponents/IButtonElement";

/**
 * HtmlButtonElement id <div /> element with behaviour of button.
 * Can have also text inside of div.
 * Example:
 *   <div Class="HtmlButtonElement">Text</div>
 *
 * @class HtmlButtonElement
 * @extends HtmlElement
 */
@engineComponent(ComponentTypes.HtmlElement)
export class HtmlButtonElement extends HtmlContainerElement implements IButtonElement {
    static Click: string = "click";
    static TouchStart: string = "touchstart";
    static TouchEnd: string = "touchend";
    static MouseOver: string = "mouseover";

    action: string;
    uiaction: Array<string>;

    private touchLock: boolean;
    private touchLockTimer: number;

    constructor() {
        super();
    }

    /**
     * Get attributes from xml and set up supported properties.
     * @method create
     * @param node is Element from xml
     * @return void
     */
    create(node: Element): void {
        super.create(node);

        /*
         * UIAction will handle click on this element and run uiaction on controller.
         * @property UIAction
         * @type string
         * @default null
         */
        if (node.hasAttribute("UIAction")) {
            this.uiaction = node.getAttribute("UIAction").split(";");
            if (!node.hasAttribute("Test")) {
                this.setTestAttributes(node.getAttribute("UIAction"));
            } else {
                this.instance.dataset["test"] = node.getAttribute("Test");
            }
            for (var i: number = 0; i < this.uiaction.length; i++) {
                HtmlController.registerUiActionElement(this.uiaction[i], this);
            }
        }

        /*
         * Action will handle click on this element and run action on controller.
         * @property Action
         * @type string
         * @default null
         */
        if (node.hasAttribute("Action")) {
            this.action = node.getAttribute("Action");
            this.setTestAttributes(node.getAttribute("Action"));

            HtmlController.registerActionElement(node.getAttribute("Action"), this);
        }

        /*
         * Test is html data attribute for automated testing support.
         * @property Test
         * @type string
         * @default null
         */
        if (node.hasAttribute("Test")) {
            this.instance.dataset["test"] = node.getAttribute("Test");
        }

        if (this.action != null || this.uiaction != null) {
            this.instance.addEventListener(HtmlButtonElement.Click, this.onClick.bind(this));
            if (this.action != null) {
                this.instance.classList.add(HtmlController.DisabledClass);
            }

            //sticky hover fix
            this.instance.addEventListener(HtmlButtonElement.TouchStart, this.onTouchStart.bind(this));
            this.instance.addEventListener(HtmlButtonElement.TouchEnd, this.onTouchEnd.bind(this));

            this.instance.addEventListener("gesturestart", Scaling.preventPropagation);
            this.instance.addEventListener("gesturechange", Scaling.preventPropagation);
            this.instance.addEventListener("gestureend", Scaling.preventPropagation);
            this.instance.addEventListener("touchmove", Scaling.preventPropagation);

            if (AppContext.gameDefinition.ClientHostType !== "Mobile") {
                this.instance.addEventListener(HtmlButtonElement.MouseOver, this.onMouseOver.bind(this));
            }
        }
    }

    setTestAttributes(action: string): void {
        this.instance.dataset["test"] = this.renameTestAttributeValue(action);
    }
    renameTestAttributeValue(action: string): string {
        let isGamblePortraitButton: boolean =
            action.indexOf("GAMBLE_") != -1 && this.instance.classList.contains("btnGamble");
        action = isGamblePortraitButton ? `${action}_PORTRAIT` : action;
        switch (action) {
            case "QUICKSPIN_DIALOG_OFF":
                if (this.instance.classList.contains("smallCloseBtn")) return "QUICKSPIN_DIALOG_CLOSE";
                return "QUICKSPIN_DIALOG_NO";
            case "QUICKSPIN_TOGGLE;QUICKSPIN_DIALOG_OFF":
                return "QUICKSPIN_DIALOG_YES";
            case "SETTINGS_OFF":
                if (this.instance.classList.contains("smallCloseBtn")) return "SETTINGS_CLOSE";
                if (
                    this.instance.classList.contains("dialogBtn") ||
                    this.instance.classList.contains("settingsPageBtn")
                )
                    return "SETTINGS_OK";
                return "SETTINGS_BACK";
            case "QUICKSPIN_TOGGLE":
                return "SETTINGS_TOGGLE";
            case "EMPTY":
                return "SIMILAR_GAMES_EXIT";
            case "EXIT2":
                return "EXIT_GAME";
            case "EXIT":
                return "HOME";
            case "HELP_OFF":
                if (this.instance.classList.contains("closeBtn")) return "HELP_CLOSE";
                return "HELP_BACK";
            case "PAYTABLE_OFF":
                if (this.instance.classList.contains("closeBtn")) return "PAYTABLE_CLOSE";
                return "PAYTABLE_BACK";
            case "SETTINGS_ON":
                return "SETTINGS";
            case "HELP_ON":
                return "HELP";
            case "PAYTABLE_ON":
                return "PAYTABLE";
            default:
                return action;
        }
    }

    registerEvents(): void {
        if (this.action != null || this.uiaction != null) {
            this.instance.addEventListener(HtmlButtonElement.Click, this.onClick.bind(this));
            if (this.action != null) {
                this.instance.classList.add(HtmlController.DisabledClass);
            }
        }
    }

    /**
     * Handler for click event.
     * Dispatch action for HtmlController.
     * @method onClick
     * @return void
     */
    onClick(e?: Event): void {
        if (DEBUG) {
            this.log("onClick action: " + this.action + " uiaction: " + this.uiaction);
        }

        if (this.instance != null && !this.instance.classList.contains(HtmlController.DisabledClass)) {
            if (e && !e.isTrusted && AppContext.gameConfiguration.DisableUntrustedClicks) {
                //due to BUG 2225
                if (AppContext.gameDefinition.ClientHostType !== "Mobile") {
                    this.log(`Untrusted action: ${this.action}, ui: ${this.action}`);
                    return;
                }
            }
            if (e && e.isTrusted) {
                DataManager.addTrustedUserAction(this.action);
            }
            if (this.action != null) {
                HtmlController.dispatchAction(this.action);
            }

            if (this.uiaction != null) {
                for (var i: number = 0; i < this.uiaction.length; i++) {
                    HtmlController.dispatchUiAction(this.uiaction[i]);
                }
            }
        }
    }

    onTouchStart(): void {
        clearTimeout(this.touchLockTimer);
        this.touchLock = true;

        if (this.instance != null && !this.instance.classList.contains("noHover")) {
            this.instance.classList.add("noHover");
        }
    }

    onTouchEnd(): void {
        this.touchLockTimer = setTimeout(() => {
            this.touchLock = false;
        }, 20);
    }

    onMouseOver(): void {
        if (this.instance != null && this.instance.classList.contains("noHover") && !this.touchLock) {
            this.instance.classList.remove("noHover");
        }
    }

    /**
     * Enable / disable button.
     * @method changeState
     * @param value boolean
     * @return void
     */
    changeState(value: boolean): void {
        if (this.instance != null) {
            if (value) {
                this.instance.classList.remove(HtmlController.DisabledClass);
            } else {
                this.instance.classList.add(HtmlController.DisabledClass);
            }
        } else {
            if (DEBUG) {
                this.log("ERROR no instance");
            }
        }
    }
}



/** WEBPACK FOOTER **
 ** ./Engine/Game/HTMLComponents/Elements/HtmlButtonElement.ts
 **/